import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetRecoilState } from 'recoil';

import { teamApi } from '../api';
import { TEAM_ASSIGNED_TASKS_QUERY_KEY, TEAM_TASKS_QUERY_KEY } from '../queries';
import { eventsSnackBarAtom } from '../recoil';
import { translateI18n } from '../../i18n';

interface DeleteTeamTaskParams {
  teamId: string;
  boardId: string;
  taskId: string;
  showSnackbar?: boolean;
}

export const useDeleteTeamTask = () => {
  const queryClient = useQueryClient();
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);

  const deleteTeamTaskMutation = useMutation({
    mutationFn: ({ teamId, boardId, taskId }: DeleteTeamTaskParams) => {
      return teamApi.deleteTeamTask(teamId, boardId, taskId).then(({ data }) => data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['lifestack.todo.tasks'] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });

      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.deleted'), type: 'success', icon: 'task' });
      }
    },
    onError: (_error, variables) => {
      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.deleteFailed'), type: 'error', icon: 'task' });
      }
    },
  });

  return {
    deleteTeamTask: deleteTeamTaskMutation.mutateAsync,
    isDeletingTeamTask: deleteTeamTaskMutation.isPending,
    ...deleteTeamTaskMutation,
  };
};
